import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BsCheckCircle, BsWallet2, BsPeople, BsLightningCharge, BsShieldCheck, BsLightbulb } from 'react-icons/bs';

interface DashboardCardsProps {
    projectsCount: number;
    completionPercent: number;
    riskStatus: '🟢 On Track' | '🟡 At Risk' | '🔴 Delayed' | '🔴 No Active Projects';

    totalAllocated: number;
    escrowLocked: number;
    escrowAvailable: number;
    spentSoFar: number;

    totalHired: number;
    activeHires: number;

    tasksCompletedThisWeek: number;
    tasksVelocityPercent?: number;
    avgTurnaroundHours: string | number;

    paymentReliability: number;
    communicationRating: number;
    disputeRate: number;
    aiSuggestion?: string;
}

const DashboardCards: React.FC<DashboardCardsProps> = ({
    projectsCount,
    completionPercent,
    riskStatus,
    totalAllocated,
    escrowLocked,
    escrowAvailable,
    spentSoFar,
    totalHired,
    activeHires,
    tasksCompletedThisWeek,
    tasksVelocityPercent,
    avgTurnaroundHours,
    paymentReliability,
    communicationRating,
    disputeRate,
    aiSuggestion
}) => {
    const navigate = useNavigate();

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">

            {/* 1. Active Projects */}
            <motion.div variants={cardVariants} className="group bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur-md relative overflow-hidden hover:shadow-[0_0_20px_rgba(212,175,55,0.15)] transition-all duration-300">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-white/60 text-sm font-medium uppercase tracking-wider flex items-center gap-2">
                        <BsCheckCircle className="text-premium-gold" /> Active Projects
                    </h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${riskStatus.includes('On Track') ? 'bg-green-500/20 text-green-400' :
                        riskStatus.includes('At Risk') ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                        {riskStatus}
                    </span>
                </div>
                <div className="flex items-end gap-4 mb-3">
                    <span className="text-4xl font-serif font-bold text-white">{projectsCount}</span>
                </div>
                <div className="w-full bg-black/40 rounded-full h-1.5 mb-1">
                    <div className="bg-premium-gold h-1.5 rounded-full" style={{ width: `${completionPercent}%` }}></div>
                </div>
                <div className="text-xs text-white/50 text-right">{completionPercent}% Overall Completion</div>
            </motion.div>

            {/* 2. Escrow Balance */}
            <motion.div variants={cardVariants} className="group bg-gradient-to-br from-white/10 to-transparent border border-white/10 rounded-xl p-6 backdrop-blur-md relative overflow-hidden hover:shadow-[0_0_20px_rgba(212,175,55,0.15)] transition-all duration-300">
                <div className="flex justify-between items-start mb-2">
                    <h3 className="text-white/60 text-sm font-medium uppercase tracking-wider flex items-center gap-2">
                        <BsWallet2 className="text-premium-gold" /> Escrow Balance
                    </h3>
                </div>
                <div className="text-3xl font-serif font-bold text-premium-gold mb-4">
                    ${escrowAvailable.toLocaleString()} <span className="text-lg text-white/40">Available</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                        <div className="text-white/40">Total Allocated</div>
                        <div className="text-white font-medium">${totalAllocated.toLocaleString()}</div>
                    </div>
                    <div>
                        <div className="text-white/40">Locked</div>
                        <div className="text-white font-medium">${escrowLocked.toLocaleString()}</div>
                    </div>
                    <div>
                        <div className="text-white/40">Spent</div>
                        <div className="text-white font-medium">${spentSoFar.toLocaleString()}</div>
                    </div>
                </div>
            </motion.div>

            {/* 3. Talent Hired */}
            <motion.div variants={cardVariants} className="group bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur-md relative overflow-hidden hover:shadow-[0_0_20px_rgba(212,175,55,0.15)] transition-all duration-300">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-white/60 text-sm font-medium uppercase tracking-wider flex items-center gap-2">
                        <BsPeople className="text-premium-gold" /> Talent Hired
                    </h3>
                </div>
                <div className="flex items-end gap-3 mb-4">
                    <span className="text-4xl font-serif font-bold text-white">{activeHires}</span>
                    <span className="text-sm text-white/50 mb-1">/ {totalHired} Total</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                    {/* Real talented icons will be mapped here if we passed their avatars, omitting fakes for now */}
                    {activeHires > 0 && (
                        <span className="text-xs text-premium-gold bg-premium-gold/10 px-2 py-0.5 rounded-full border border-premium-gold/20">
                            Active Collaborators
                        </span>
                    )}
                </div>
            </motion.div>

            {/* 4. Project Velocity */}
            <motion.div variants={cardVariants} className="group bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur-md relative overflow-hidden hover:shadow-[0_0_20px_rgba(212,175,55,0.15)] transition-all duration-300">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-white/60 text-sm font-medium uppercase tracking-wider flex items-center gap-2">
                        <BsLightningCharge className="text-premium-gold" /> Project Velocity
                    </h3>
                </div>
                <div className="flex justify-between items-center mb-2">
                    <div className="text-3xl font-serif font-bold text-white">{tasksCompletedThisWeek}</div>
                    {tasksVelocityPercent !== undefined && (
                        <div className={`text-xs px-2 py-1 rounded-full ${tasksVelocityPercent >= 0 ? 'text-green-400 bg-green-500/10' : 'text-red-400 bg-red-500/10'}`}>
                            {tasksVelocityPercent >= 0 ? '+' : ''}{tasksVelocityPercent}% vs last week
                        </div>
                    )}
                </div>
                <p className="text-white/50 text-sm">Tasks completed this week</p>
                <div className="mt-4 pt-4 border-t border-white/10 flex justify-between">
                    <span className="text-xs text-white/50">Avg Turnaround</span>
                    <span className="text-xs font-bold text-white">{avgTurnaroundHours}</span>
                </div>
            </motion.div>

            {/* 5. Reliability Score */}
            <motion.div variants={cardVariants} className="group bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur-md relative overflow-hidden hover:shadow-[0_0_20px_rgba(212,175,55,0.15)] transition-all duration-300">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-white/60 text-sm font-medium uppercase tracking-wider flex items-center gap-2">
                        <BsShieldCheck className="text-premium-gold" /> Reliability Score
                    </h3>
                    <div className="text-premium-gold font-bold text-xl">{((paymentReliability + communicationRating) / 2).toFixed(1)}/10</div>
                </div>
                <div className="space-y-2 mt-4">
                    <div className="flex justify-between text-xs">
                        <span className="text-white/60">Payment History</span>
                        <span className="text-white font-medium text-green-400">{paymentReliability}/10</span>
                    </div>
                    <div className="flex justify-between text-xs">
                        <span className="text-white/60">Communication</span>
                        <span className="text-white font-medium">{communicationRating}/10</span>
                    </div>
                    <div className="flex justify-between text-xs">
                        <span className="text-white/60">Disputes</span>
                        <span className="text-white font-medium text-green-400">{disputeRate}%</span>
                    </div>
                </div>
            </motion.div>

            {/* 6. AI Opportunity Suggestions */}
            <motion.div variants={cardVariants} className="group bg-gradient-to-br from-premium-gold/10 to-transparent border border-premium-gold/30 rounded-xl p-6 backdrop-blur-md relative overflow-hidden hover:shadow-[0_0_20px_rgba(212,175,55,0.2)] transition-all duration-300">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                    <BsLightbulb size={64} className="text-premium-gold" />
                </div>
                <h3 className="text-premium-gold text-sm font-medium uppercase tracking-wider flex items-center gap-2 mb-4">
                    <BsLightbulb /> AI Suggestions
                </h3>
                <p className="text-sm text-white/80 leading-relaxed relative z-10">
                    {aiSuggestion || "Connect with talent and complete projects to receive AI-driven insights on spending and team composition."}
                </p>
                <div className="mt-4 relative z-10">
                    <button
                        onClick={() => navigate('/talent')}
                        className="text-xs text-premium-wood-dark bg-premium-gold px-4 py-2 rounded-lg font-bold hover:bg-white transition-colors"
                    >
                        Find Candidates
                    </button>
                </div>
            </motion.div>

        </div>
    );
};

export default DashboardCards;
